import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Send, Loader2, Calendar, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/components/ui/Toast';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { useApplyForLeave, useLeaveBalances } from '@/hooks/useLeave';
import { LEAVE_TYPES, LeaveType } from '@/types';
import { differenceInDays } from '@/lib/utils';
import { cn } from '@/lib/utils';

export function LeaveApplicationForm() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const { data: leaveBalances } = useLeaveBalances();
  const applyForLeave = useApplyForLeave();

  const [formData, setFormData] = useState({
    leaveType: '' as LeaveType | '',
    startDate: '',
    endDate: '',
    hoursPerDay: 7.6,
    reason: '',
  });

  const [calculated, setCalculated] = useState({
    totalDays: 0,
    totalHours: 0,
  });

  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const days = Math.max(0, differenceInDays(end, start) + 1);
      const hours = days * formData.hoursPerDay;
      setCalculated({ totalDays: days, totalHours: hours });
    }
  }, [formData.startDate, formData.endDate, formData.hoursPerDay]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const getBalanceForType = (type: LeaveType) => {
    return leaveBalances?.find(b => b.type === type);
  };

  const selectedBalance = formData.leaveType ? getBalanceForType(formData.leaveType) : null;
  const insufficientBalance = selectedBalance && calculated.totalHours > selectedBalance.balance;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.leaveType || !formData.startDate || !formData.endDate) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    if (insufficientBalance) {
      if (!confirm('You have insufficient leave balance. Submit anyway?')) {
        return;
      }
    }

    try {
      await applyForLeave.mutateAsync({
        leaveType: formData.leaveType,
        startDate: formData.startDate,
        endDate: formData.endDate,
        hoursPerDay: formData.hoursPerDay,
        reason: formData.reason,
      });
      toast({
        title: 'Success',
        description: 'Leave application submitted successfully',
        variant: 'success',
      });
      navigate('/payroll/leave');
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to submit leave application',
        variant: 'destructive',
      });
    }
  };

  const isSubmitting = applyForLeave.isPending;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Apply for Leave</h1>
          <p className="text-sm text-muted-foreground">
            Submit a new leave application
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Leave Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="leaveType">Leave Type *</Label>
                  <Select
                    value={formData.leaveType}
                    onValueChange={(value) => handleChange('leaveType', value)}
                  >
                    <SelectTrigger id="leaveType">
                      <SelectValue placeholder="Select leave type" />
                    </SelectTrigger>
                    <SelectContent>
                      {LEAVE_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={(e) => handleChange('startDate', e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="endDate">End Date *</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={formData.endDate}
                      onChange={(e) => handleChange('endDate', e.target.value)}
                      required
                      min={formData.startDate}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="hoursPerDay">Hours per Day *</Label>
                  <Input
                    id="hoursPerDay"
                    type="number"
                    step="0.1"
                    min="0.1"
                    max="24"
                    value={formData.hoursPerDay}
                    onChange={(e) => handleChange('hoursPerDay', parseFloat(e.target.value) || 0)}
                    required
                  />
                  <p className="text-sm text-muted-foreground">
                    Standard full-time day is 7.6 hours
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reason">Reason</Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => handleChange('reason', e.target.value)}
                    placeholder="Optional reason for leave"
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Summary Card */}
          <div>
            <Card className="sticky top-4">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Leave Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedBalance && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="text-sm text-muted-foreground">Available Balance</p>
                    <p className={cn(
                      "text-xl font-bold",
                      selectedBalance.balance < 20 && "text-warning",
                      selectedBalance.balance <= 0 && "text-destructive"
                    )}>
                      {selectedBalance.balance} {selectedBalance.unit}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Duration</span>
                    <span>{calculated.totalDays} day{calculated.totalDays !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Hours per Day</span>
                    <span>{formData.hoursPerDay} hrs</span>
                  </div>
                </div>

                <div className="border-t pt-2">
                  <div className="flex justify-between">
                    <span className="font-medium">Total Hours</span>
                    <span className={cn(
                      "text-lg font-bold",
                      insufficientBalance && "text-destructive"
                    )}>
                      {calculated.totalHours} hrs
                    </span>
                  </div>
                </div>

                {insufficientBalance && (
                  <div className="bg-destructive/10 p-3 rounded-lg flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-destructive">Insufficient Balance</p>
                      <p className="text-xs text-destructive/80">
                        You don't have enough leave balance for this request.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <Send className="mr-2 h-4 w-4" />
            Submit Application
          </Button>
        </div>
      </form>
    </div>
  );
}
